import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../cn";

/** Right-side overlay used for spend ask detail, KYC detail, funder approval, etc. */
export function Drawer({
  open,
  onOpenChange,
  children,
  width = "640px",
  className,
  variant = "modal",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  width?: string;
  className?: string;
  /**
   * "non-modal" keeps the page behind the drawer visible and dimmed only
   * faintly, rather than the default full-strength overlay — for surfaces
   * like Trace Source that sit alongside the underlying list rather than
   * blocking it (Trace Source spec §3.1). Radix still traps focus/closes on
   * outside click either way; this only changes the visual weight of the
   * backdrop. Opt-in per caller — the default stays as-is for the other
   * drawers (SpendAskDrawer, RecordSpendDrawer, FunderApprovalDrawer) that
   * rely on today's stronger dim.
   */
  variant?: "modal" | "non-modal";
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 backdrop-blur-[2px] animate-fade-in",
            variant === "non-modal" ? "bg-ink/10" : "bg-ink/25",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed right-0 top-0 z-50 flex h-full flex-col border-l border-line bg-card shadow-overlay animate-slide-in-right",
            className,
          )}
          style={{ width, maxWidth: "100vw" }}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const DrawerHeader = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string; tabIndex?: number }
>(function DrawerHeader({ children, className, tabIndex }, ref) {
  return (
    <div
      ref={ref}
      tabIndex={tabIndex}
      className={cn("flex items-start justify-between gap-4 border-b border-line px-6 py-4", className)}
    >
      <div className="min-w-0">{children}</div>
      {/* h-11 w-11 (44px), not the icon's own 16px: this is a shared
          component used by every Drawer in the app (RecordSpendDrawer,
          QueryReplyDrawer, FunderApprovalDrawer, NotificationsBell, ...) -
          a ~24px hit target here was the most common single mobile
          touch-target gap found across the whole app-wide audit. */}
      <Dialog.Close
        className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-faint transition-colors hover:bg-subtle hover:text-ink active:opacity-70"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </Dialog.Close>
    </div>
  );
});

export function DrawerBody({ children, className }: { children: React.ReactNode; className?: string }) {
  // min-h-0 is required alongside flex-1 + overflow-y-auto: without it, a flex
  // item defaults to min-height:auto and grows to fit its content instead of
  // scrolling within the parent's bounded height (a common flexbox gotcha,
  // more visible now that Trace's modal is height-capped via max-h rather
  // than the other drawers' viewport-filling h-full).
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-5", className)}>{children}</div>;
}

export function DrawerFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  // flex-wrap: a footer with 3+ buttons (e.g. Cancel / Draft / Create, one
  // with a dynamic interpolated label) has no room to fit on one line once
  // the drawer is full-viewport-width on a phone - wrapping to a second
  // row degrades gracefully, a fixed non-wrapping row doesn't. Harmless for
  // every other caller with 1-2 short buttons, which never need to wrap.
  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-2 border-t border-line bg-subtle/40 px-6 py-4", className)}>
      {children}
    </div>
  );
}

export const DrawerTitle = Dialog.Title;
export const DrawerDescription = Dialog.Description;
